// feature/lawyer-specializations.
// Frontend mirror of eds-backend/lib/permissions.js. Used to hide affordances
// from users who wouldn't be allowed to use them anyway. Backend remains the
// load-bearing gate; this is UX polish.

export const ALL_SPECIALIZATIONS = [
  "tutelas",
  "sanciones",
  "desacatos",
  "impugnaciones",
  "memoriales",
  "fallos_contestacion",
];

export const SPECIALIZATION_LABEL = {
  tutelas:             "Tutelas",
  sanciones:           "Sanciones",
  desacatos:           "Desacatos",
  impugnaciones:       "Impugnaciones",
  memoriales:          "Memoriales",
  fallos_contestacion: "Contestación de fallos",
};

export const PRIVILEGED_SPECIALIZATIONS = ["sanciones", "impugnaciones", "memoriales"];

export const TIPO_TO_SPECIALIZATION = {
  tutela:        "tutelas",
  desacato:      "desacatos",
  sancion:       "sanciones",
  fallo:         "fallos_contestacion",
  requerimiento: "tutelas",
};

// feature/single-role-audit — exported so every component can do
// multi-role checks via the same shape: rolesOf(user).includes("X")
// or ["a","b"].some(r => rolesOf(user).includes(r)). Falls back to
// [user.role] for legacy single-role JWTs (issued pre-Sprint 22).
export function rolesOf(user) {
  if (!user) return [];
  if (Array.isArray(user.roles) && user.roles.length) return user.roles;
  return user.role ? [user.role] : [];
}

function specsOf(user) {
  if (!user) return [];
  return Array.isArray(user.specializations) ? user.specializations : [];
}

export function hasPrivilegedSpecialization(user) {
  const specs = specsOf(user);
  return PRIVILEGED_SPECIALIZATIONS.some((s) => specs.includes(s));
}

export function primarySpecializationLabel(user) {
  const specs = specsOf(user);
  for (const s of PRIVILEGED_SPECIALIZATIONS) {
    if (specs.includes(s)) return SPECIALIZATION_LABEL[s] || s;
  }
  if (specs.length) return SPECIALIZATION_LABEL[specs[0]] || specs[0];
  return "tu rol";
}

export function canMarkContestadaDirectly(user, caseData) {
  if (!user || !caseData) return false;
  const roles = rolesOf(user);
  if (roles.includes("admin") || roles.includes("directora")) return true;
  if (!roles.includes("abogado")) return false;
  if (caseData.abogado_asignado_id !== user.id) return false;
  // Sprint 55d — privileged-specialization gate removed. Any assigned
  // lawyer can mark contestada now; the categoría dropdown still
  // filters by spec (availableContestadaCategorias) so tutelas-only
  // lawyers don't get impugnación / memorial / sancion_*.
  // hasPrivilegedSpecialization stays exported — used elsewhere.
  return true;
}

export function canRequestModeration(user, caseData) {
  if (!user || !caseData) return false;
  const roles = rolesOf(user);
  if (roles.includes("admin") || roles.includes("directora")) return true;
  if (!roles.includes("abogado")) return false;
  return caseData.abogado_asignado_id === user.id;
}

export function canResolveModeration(user) {
  const roles = rolesOf(user);
  return ["admin", "directora", "insumos"].some((r) => roles.includes(r));
}

// Categories the user can pick when marking contestada. Standard
// respuesta_normal/respuesta_n_a/temeridad/otra are always available;
// specialist tags unlock the matching specialist categories.
export function availableContestadaCategorias(user) {
  const specs = specsOf(user);
  const out = ["respuesta_normal", "respuesta_n_a", "temeridad"];
  if (specs.includes("impugnaciones"))   out.push("impugnacion");
  if (specs.includes("memoriales"))      out.push("memorial_cumplimiento");
  if (specs.includes("sanciones"))       out.push("sancion_respondida", "sancion_inaplicacion");
  out.push("otra");
  return [...new Set(out)];
}

export const CONTESTADA_LABELS = {
  respuesta_normal:      "Respuesta normal",
  respuesta_n_a:         "N/A (no aplica)",
  temeridad:             "Temeridad",
  impugnacion:           "Impugnación",
  memorial_cumplimiento: "Memorial de cumplimiento",
  sancion_respondida:    "Sanción respondida",
  sancion_inaplicacion:  "Inaplicación de sanción",
  otra:                  "Otra",
};
